import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { FileUploadComponent } from '../../fileupload/fileupload.component';
import { environment } from '../../../../environments/environment';
import { ProcessingStateService } from '../../../services/processing-state.service';
import { TranslationStatusService } from '../../../services/translation-status.service';
import {
    TranslationsService,
    type UploadTranslationFile,
    type UploadTranslationRequest,
    type UploadTranslationResponse
} from '../../../services/translations.service';

@Component({
    selector: 'app-upload',
    standalone: true,
    imports: [CommonModule, FormsModule, FileUploadComponent],
    templateUrl: './upload.component.html',
    styleUrl: './upload.component.css',
})
export class UploadComponent {
    private readonly successResponseTypeId = 100;

    @ViewChild('inputFileUpload') private inputFileUpload?: FileUploadComponent;
    @ViewChild('sampleDataFilesUpload') private sampleDataFilesUpload?: FileUploadComponent;
    @ViewChild('expectedOutputFileUpload') private expectedOutputFileUpload?: FileUploadComponent;

    constructor(
        private readonly processingState: ProcessingStateService,
        private readonly translationStatusService: TranslationStatusService,
        private readonly translationsService: TranslationsService
    ) { }

    readonly inputFileAccept = environment.uploadConfig.inputFile.accept;
    readonly inputFileMultiple = environment.uploadConfig.inputFile.multiple;
    readonly sampleDataFilesAccept = environment.uploadConfig.sampleDataFiles.accept;
    readonly sampleDataFilesMultiple = environment.uploadConfig.sampleDataFiles.multiple;
    readonly expectedOutputFileAccept = environment.uploadConfig.expectedOutputFile.accept;
    readonly expectedOutputFileMultiple = environment.uploadConfig.expectedOutputFile.multiple;
    readonly maxTotalFileSizeBytes = environment.uploadConfig.maxTotalFileSizeBytes;

    translationName = '';
    inputFile: File | null = null;
    sampleDataFiles: File[] = [];
    expectedOutputFile: File | null = null;
    willValidateTranslation: boolean = false;
    uploadErrorMessage: string | null = null;
    uploadSuccessMessage: string | null = null;
    uploadValidationErrors: string[] = [];

    get canUpload(): boolean {
        return (
            this.translationName.trim().length > 0 &&
            !!this.inputFile &&
            (this.willValidateTranslation ||
                (this.sampleDataFiles.length > 0 &&
                    !!this.expectedOutputFile)) &&
            this.fileSizeErrorMessage === null
        );
    }

    get isProcessing(): boolean {
        return this.processingState.isProcessing();
    }

    get fileSizeErrorMessage(): string | null {
        const selectedFiles = this.getSelectedUploadFiles();
        if (selectedFiles.length === 0) return null;

        const totalFileSize = this.getTotalFileSize(selectedFiles);
        if (totalFileSize <= this.maxTotalFileSizeBytes) return null;

        return `Selected files total ${this.formatFileSize(totalFileSize)}. The maximum total upload size is ${this.formatFileSize(this.maxTotalFileSizeBytes)}.`;
    }

    onInputFileChange(files: File[]): void {
        this.clearUploadFeedback();
        this.inputFile = files[0] ?? null;
    }

    onSampleDataFilesChange(files: File[]): void {
        this.clearUploadFeedback();
        this.sampleDataFiles = files;
    }

    onExpectedOutputFileChange(files: File[]): void {
        this.clearUploadFeedback();
        this.expectedOutputFile = files[0] ?? null;
    }

    onWillValidateTranslationChange(willValidateTranslation: boolean): void {
        this.clearUploadFeedback();
        this.willValidateTranslation = willValidateTranslation;

        if (willValidateTranslation) {
            this.sampleDataFiles = [];
            this.expectedOutputFile = null;
        }
    }

    async onUpload(): Promise<void> {
        if (!this.canUpload || !this.inputFile) return;

        this.processingState.setProcessing(true);
        this.clearUploadFeedback();

        try {
            const uploadRequest = await this.buildUploadRequest(
                this.inputFile,
                this.sampleDataFiles,
                this.expectedOutputFile,
                this.translationName.trim(),
                this.willValidateTranslation
            );

            const response = await firstValueFrom(this.translationsService.uploadTranslation(uploadRequest));
            const validationErrors = this.getInvalidReasons(response);

            if (validationErrors.length > 0) {
                this.uploadValidationErrors = validationErrors;
                this.uploadErrorMessage = response.responseTypeDescription ?? 'Invalid request.';
                return;
            }

            if (!this.isSuccessResponse(response)) {
                this.uploadErrorMessage = response.responseTypeDescription ?? 'Unable to upload translation.';
                return;
            }

            const friendlyName = this.translationName.trim();
            const submissionId = this.getGuidId(response);
            this.resetUploadForm();
            this.uploadSuccessMessage = 'Translation uploaded successfully.';

            if (submissionId) {
                void this.translationStatusService.trackSubmission(submissionId, friendlyName);
            }
        } catch (error) {
            const validationErrors = this.getInvalidReasonsFromError(error);

            if (validationErrors.length > 0) {
                this.uploadValidationErrors = validationErrors;
                this.uploadErrorMessage = this.getResponseTypeDescriptionFromError(error) ?? 'Invalid request.';
                return;
            }

            this.uploadErrorMessage = 'Unable to upload translation.';
        } finally {
            this.processingState.setProcessing(false);
        }
    }

    private async buildUploadRequest(
        inputFile: File,
        sampleDataFiles: File[],
        expectedOutputFile: File | null,
        friendlyName: string,
        submitterWillValidate: boolean
    ): Promise<UploadTranslationRequest> {
        return {
            originalFile: await this.toUploadFilePayload(inputFile),
            sampleDataFiles: await Promise.all(
                sampleDataFiles.map(file => this.toUploadFilePayload(file))
            ),
            expectedOutputFile: expectedOutputFile
                ? await this.toUploadFilePayload(expectedOutputFile)
                : null,
            friendlyName,
            submitterWillValidate
        };
    }

    private async toUploadFilePayload(file: File): Promise<UploadTranslationFile> {
        return {
            fileName: file.name,
            base64File: await this.toBase64File(file),
        };
    }

    private toBase64File(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onerror = () => reject(reader.error ?? new Error(`Unable to read ${file.name}.`));
            reader.onload = () => {
                if (typeof reader.result !== 'string') {
                    reject(new Error(`Unable to read ${file.name} as base64.`));
                    return;
                }

                const [, base64File = reader.result] = reader.result.split(',', 2);
                resolve(base64File);
            };

            reader.readAsDataURL(file);
        });
    }

    private getInvalidReasons(response: UploadTranslationResponse | null | undefined): string[] {
        return response?.invalidReasons?.filter((reason) => reason.trim().length > 0) ?? [];
    }

    private getInvalidReasonsFromError(error: unknown): string[] {
        if (!(error instanceof HttpErrorResponse)) return [];

        return this.getInvalidReasons(error.error as UploadTranslationResponse | null | undefined);
    }

    private isSuccessResponse(response: UploadTranslationResponse | null | undefined): boolean {
        return (
            response?.responseTypeId === this.successResponseTypeId ||
            response?.responseTypeDescription?.toLowerCase() === 'success'
        );
    }

    private getResponseTypeDescriptionFromError(error: unknown): string | null {
        if (!(error instanceof HttpErrorResponse)) return null;

        const response = error.error as UploadTranslationResponse | null | undefined;
        const description = response?.responseTypeDescription;
        return description && description.trim().length > 0 ? description : null;
    }

    private getGuidId(response: UploadTranslationResponse | null | undefined): string | null {
        const guidId = response?.guidId;
        return guidId && guidId.trim().length > 0 ? guidId.trim() : null;
    }

    private clearUploadFeedback(): void {
        this.uploadErrorMessage = null;
        this.uploadSuccessMessage = null;
        this.uploadValidationErrors = [];
    }

    private getSelectedUploadFiles(): File[] {
        const selectedFiles: File[] = [];

        if (this.inputFile) {
            selectedFiles.push(this.inputFile);
        }

        if (!this.willValidateTranslation) {
            selectedFiles.push(...this.sampleDataFiles);

            if (this.expectedOutputFile) {
                selectedFiles.push(this.expectedOutputFile);
            }
        }

        return selectedFiles;
    }

    private getTotalFileSize(files: File[]): number {
        return files.reduce((totalFileSize, file) => totalFileSize + file.size, 0);
    }

    private formatFileSize(bytes: number): string {
        const units = ['bytes', 'KiB', 'MiB', 'GiB'];
        let size = bytes;
        let unitIndex = 0;

        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }

        if (unitIndex === 0) return `${size} ${units[unitIndex]}`;

        const precision = size >= 10 ? 0 : 1;
        return `${size.toFixed(precision)} ${units[unitIndex]}`;
    }

    private resetUploadForm(): void {
        this.translationName = '';
        this.inputFile = null;
        this.sampleDataFiles = [];
        this.expectedOutputFile = null;
        this.willValidateTranslation = false;

        this.inputFileUpload?.clearSelection();
        this.sampleDataFilesUpload?.clearSelection();
        this.expectedOutputFileUpload?.clearSelection();
    }
}
